'use client'

import React, { useId, useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'

// Ikony
import Facebook from 'public/icons/social-media/facebook.svg'
import Instagram from 'public/icons/social-media/instagram.svg'
import Linkedin from 'public/icons/social-media/linkedin.svg'
import X from 'public/icons/social-media/x.svg'
import Share from 'public/icons/interface/share.svg'
import Arrow from 'public/icons/arrows/expanded.svg'

export default function SocialShare({ title }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState(null)
  const menuRef = useRef(null)
  const pathname = usePathname()
  const menuId = useId()
  const translate = useTranslateEntry()

  useEffect(() => {
    setUrl(window.location.origin + pathname)
  }, [pathname])

  const shareLinks = [
    {
      name: 'Facebook',
      icon: <Facebook className="text-basic-black min-w-20" />,
      href: url ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` : '#',
    },
    {
      name: 'X',
      icon: <X className="text-basic-black min-w-18" />,
      href: url
        ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
        : '#',
    },
    {
      name: 'Instagram',
      icon: <Instagram className="text-basic-black min-w-24" />,
      href: 'https://www.instagram.com/',
    },
    {
      name: 'LinkedIn',
      icon: <Linkedin className="text-basic-black min-w-20" />,
      href: url
        ? `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
        : '#',
    },
  ]

  const toggleMenu = () => setOpen((prev) => !prev)

  useEffect(() => {
    if (!menuRef.current) return
    if (open) gsap.fromTo(menuRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 })
    else gsap.to(menuRef.current, { opacity: 0, duration: 0.3 })
  }, [open])

  return (
    <div className="relative inline-block text-left">
      <button onClick={toggleMenu} aria-controls={menuId} aria-expanded={open} className="flex items-center gap-x-4">
        <Share />
        <Typography variant="h3-bold" component="span" color="text-basic-black">
          {translate('T_SHARE')}
        </Typography>
        <Arrow className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && url && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          className="absolute right-0 mt-9 p-16 w-fit border bg-basic-white rounded-[4px] gap-x-16 flex items-center shadow-[0px_4px_14px_0px_#0000001A] opacity-0 z-50"
        >
          {shareLinks.map((item, index) => (
            <React.Fragment key={item.name}>
              <Link
                href={item.href}
                target="_blank"
                role="menuitem"
                className="hover:opacity-70"
                aria-label={item.name}
              >
                {item.icon}
              </Link>
              {index < shareLinks.length - 1 && <hr className="w-1 h-28 bg-basic-gray10 text-basic-gray10" />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}
